import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Filter, Scope } from './filter';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { share } from 'rxjs/operators';
import { Cacheable, CacheBuster } from 'ts-cacheable';

const filtersCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class FilterService
{
  public filterChangedSource = new Subject<Filter>();

  /**
   * Filter changed observable.
   */
  public filterChanged = this.filterChangedSource.asObservable();

  constructor(private http: HttpClient, private authenticationService: AuthenticationService) { }

  /**
   * Get all Program's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedProgramFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Program`);
  }

  /**
   * Get all Task's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedTaskFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Task`);
  }

  /**
   * Get all Referral Task's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedReferralTaskFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Referral-Task`);
  }

  /**
   * Get all Case's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedCaseFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Case`);
  }

  /**
   * Get all Assessments's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedAssessmentFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Assessment`);
  }

  /**
   * Get all Member's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedMemberFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Member`);
  }

  /**
   * Get all Event's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedEventFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Event`);
  }

  /**
   * Get all Dashboard's cached filters for a user
   */
  @Cacheable(
    {
      cacheBusterObserver: filtersCacheBuster
    })
  public getCachedDashboardFilters(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/Dashboard`);
  }

  /**
   * Save filter to database
   */
  @CacheBuster(
    {
      cacheBusterNotifier: filtersCacheBuster
    })
  public saveFilter(filter: Filter): any
  {
    // pipe/share here is to prevent multiple http calls from being made due to multiple subs.
    const theFilter = this.http.post<Filter>('/filter', filter).pipe(share());

    theFilter.subscribe({
      next: (f: any) =>
      {
        this.filterChangedSource.next(f);
      }
    });
    return theFilter;
  }

  /**
   * Delete a Filter.
   *
   * @param id Filter id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: filtersCacheBuster
    })
  public deleteFilter(id: number): Observable<any>
  {
    return this.http.delete<any>(`/filter/${id}`);
  }

  /**
   * Get all filters for a user
   */
  public getFilters(userName: string, category: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/${category}`);
  }

  /**
   * Get filters for a group and a screen
   */
  public getFilterByScreen(category: string, screen: string): Observable<Filter>
  {
    return this.http.get<Filter>(`/filters/screen/${category}/${screen}`);
  }

  /**
   * Get filter by id
   */
  public getFilter(filterId: number): Observable<Filter>
  {
    return this.http.get<Filter>(`/filter/${filterId}`);
  }

  /**
   * Constructs and returns a Filter object.
   *
   * @param state Grid operations state
   * @param filterName Filter name
   * @param category Data category (table name)
   * @param scope Filter scope (Global/Group/User)
   */
  public createFilter(state: any, filterName: string, category: string, scope: Scope, organization: number, id: string,
    screen: string): Filter
  {
    const filter: any = {};
    filter.id = id;
    filter.name = filterName;
    filter.state = state;
    filter.category = category;
    filter.createdBy = this.authenticationService.currentUser.userName;
    filter.createdOn = new Date();
    filter.scope = scope;
    filter.organization = organization;
    filter.screen = screen;

    return filter;
  }

  /**
   * Get all Commununity Services cached filters for a user
   */
  public getCommunityServices(userName: string): Observable<Filter[]>
  {
    return this.http.get<Filter[]>(`/filters/${userName}/CommunityService`);
  }
}
